// COPY of functions/reassign/discounts.js (the dashboard cannot import from functions/).
// Which of the original booking's discounts move to a replacement booking. Pure.
// Keep the two files identical apart from this header and the export line.
//
// Discounts a booking can carry (see the customer app's CheckoutScreen price build,
// applied in this order — each later step overwrites price.codeId / price.code):
//   promo code      redeemedPromoCode, offer by codeId (or by promoCode text once a later
//                   step overwrote codeId). Can be scoped by sp_id / services / washingTypes.
//                   Voucher-type (`cashback`) and BOGO-type promos give no discount now but a
//                   reward on completion (price.cashback*, price.bogoPercent*) — same scope.
//   wallet voucher  voucher_id + redeemedVoucher. Never provider-scoped; has minAmount.
//   wallet BOGO     bogo_id + redeemedBogo. The wallet record carries sp_id / serviceId / washingTypes.
//   loyalty points  redeemedPoints ("Cashback" in the app). Never scoped.
//   wallet credit   redeemedCredit. Never scoped.
//
// Rule: never-scoped discounts always carry; scoped ones carry only while still valid for
// the new provider / service / washing type (the app's isOfferActive steps 7-9 — active,
// expiry, count and first-order eligibility are NOT re-checked: the customer already
// redeemed it). A carried promo is recalculated on the new price with the offer's current
// terms (the app's calculateOfferDiscount); everything else keeps its AED amount. Amounts
// are re-applied in checkout order and capped at what is left of the new price.

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round(n(v) * 100) / 100;
const lower = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

const SPECIAL_CODES = ['loyalty', 'wallet', 'voucher', 'bogo', 'subscription'];
const isSpecialCode = (v) => SPECIAL_CODES.includes(lower(v));

// Every price field this module owns. Anything not listed is left alone.
const DISCOUNT_FIELDS = [
    'discount', 'codeId', 'code', 'promoCode', 'redeemedPromoCode',
    'voucher_id', 'redeemedVoucher',
    'bogo_id', 'redeemedBogo', 'bogoDiscountAmount', 'bogoVoucherId',
    'redeemedPoints', 'pointsPalance', 'redeemedCredit',
    'cashback', 'cashbackExpiryDays', 'cashbackNumberOfSplits',
    'bogoPercent', 'bogoCap', 'bogoExpiryDays', 'bogoServiceId', 'bogoSpId', 'bogoWashingTypes',
    'freeAddonId',
];
const PROMO_REWARD_FIELDS = [
    'cashback', 'cashbackExpiryDays', 'cashbackNumberOfSplits',
    'bogoPercent', 'bogoCap', 'bogoExpiryDays', 'bogoServiceId', 'bogoSpId', 'bogoWashingTypes',
];

const keyOf = (v) => (v && typeof v === 'object' ? (v.key || v.id) : v);

/** Where to find the promo offer of this price: { id } or { code } or null. */
function promoOfferRef(op) {
    const p = op || {};
    if (p.codeId && !isSpecialCode(p.codeId)) return { id: String(p.codeId) };
    if (typeof p.promoCode === 'string' && p.promoCode.trim() && !isSpecialCode(p.promoCode)) return { code: p.promoCode };
    if (typeof p.code === 'string' && p.code.trim() && !isSpecialCode(p.code)) return { code: p.code };
    return null;
}

/** The wallet BOGO record redeemed on this booking (not the BOGO-type promo's offer id). */
const bogoRecordId = (op) => ((op && (op.bogo_id || (n(op.redeemedBogo) > 0 ? op.bogoVoucherId : null))) || null);

/** Scope check only (app isOfferActive steps 7-9). */
function scopeAllows(scoped, target) {
    if (!scoped) return false;
    const t = target || {};
    const wash = t.washingType || 'pressureWash';
    if (scoped.sp_id != null && scoped.sp_id !== '' && scoped.sp_id !== t.spId) return false;
    const services = (scoped.services || []).map(keyOf).filter(Boolean);
    const serviceId = scoped.serviceId || scoped.bogoServiceId;
    if (services.length && !services.includes(t.serviceId)) return false;
    if (serviceId && t.serviceId && serviceId !== t.serviceId) return false;
    const wts = (scoped.washingTypes || scoped.bogoWashingTypes || []).map(keyOf).filter(Boolean);
    if (wts.length && !wts.includes(wash)) return false;
    return true;
}

/** Same as the app's getOfferDiscountType. */
function offerDiscountType(offer) {
    const o = offer || {};
    if (o.discountType) return o.discountType;
    if (o.freeAddon && o.freeAddon.addonId) return 'free_addon';
    if (o.cashback) return 'cashback';
    if (o.bogo && o.bogo.percent != null) return 'bogo';
    if (o.firstOrder) return 'first_order';
    if (o.fixedAmount && o.fixedAmount.amount != null) return 'fixed_amount';
    if (o.percent != null || o.cap != null) return 'percentage';
    return null;
}

/**
 * The promo discount on `base` (service + add-ons + variation — the app's priceBeforeDiscount),
 * mirroring the app's calculateOfferDiscount. null = unknown offer shape.
 */
function promoDiscountFor(offer, base) {
    const sub = n(base);
    switch (offerDiscountType(offer)) {
        case 'percentage': {
            const p = offer.percentage != null ? offer.percentage : offer;
            const percent = n(p && p.percent != null ? p.percent : offer.percent);
            const capRaw = p && p.cap != null ? p.cap : offer.cap;
            const cap = capRaw != null ? n(capRaw) : Infinity;
            return round2(Math.min((sub * percent) / 100, cap));
        }
        case 'fixed_amount':
            return round2(Math.min(n(offer.fixedAmount && offer.fixedAmount.amount), sub));
        case 'first_order': {
            const fo = offer.firstOrder || {};
            if (fo.value == null) return 0;
            let d = fo.type === 'fixed' ? n(fo.value) : (sub * n(fo.value)) / 100;
            if (fo.type !== 'fixed' && fo.cap != null && n(fo.cap) > 0) d = Math.min(d, n(fo.cap));
            return round2(Math.min(d, sub));
        }
        case 'free_addon': case 'cashback': case 'bogo':
            return 0;
        default:
            return null;
    }
}

/** Split the original's total discount into its parts (with fallbacks for older bookings). */
function discountParts(op) {
    const p = op || {};
    const total = round2(p.discount);
    const code = lower(p.code) || lower(p.codeId);
    let points = round2(p.redeemedPoints);
    let credit = round2(p.redeemedCredit);
    const voucher = p.voucher_id ? round2(p.redeemedVoucher) : 0;
    const bogo = bogoRecordId(p) ? round2(p.redeemedBogo != null ? p.redeemedBogo : p.bogoDiscountAmount) : 0;
    // Older bookings carry only discount + code.
    if (!points && !credit && !voucher && !bogo && total > 0) {
        if (code === 'loyalty') points = total;
        else if (code === 'wallet') credit = total;
    }
    const promo = round2(Math.max(0, total - points - credit - voucher - bogo));
    return { total, points, credit, voucher, bogo, promo };
}

/**
 * @param {object} op       original booking price
 * @param {object} ctx      { promoOffer, voucherRecord, bogoRecord } — null when absent / not found
 * @param {object} target   { spId, serviceId, washingType, grossPrice, promoBase } — grossPrice =
 *                          the new price before any discount (service + add-ons + variation +
 *                          warranty + pickup); promoBase = what a promo is calculated on
 *                          (service + add-ons + variation), defaults to grossPrice
 * @returns {{
 *   fields: object,          discount fields to SET on the replacement price (others in DISCOUNT_FIELDS are removed)
 *   discount: number,        total carried discount
 *   carried: Array<{kind, amount}>,
 *   dropped: Array<{kind, amount, reason}>,
 *   releaseVoucherId: string|null, releaseBogoRecordId: string|null,   wallet records no longer used
 *   excess: { points: number, credit: number },                         redeemed but no longer applied (capped)
 * }}
 */
function resolveCarriedDiscounts(op, ctx, target) {
    const p = op || {};
    const c = ctx || {};
    const t = target || {};
    const parts = discountParts(p);
    const carried = []; const dropped = [];
    const fields = {};
    let remaining = round2(Math.max(0, n(t.grossPrice)));
    const take = (amount) => { const a = round2(Math.min(amount, remaining)); remaining = round2(remaining - a); return a; };

    // 1. promo code (its discount and any completion reward share the offer's scope)
    const ref = promoOfferRef(p);
    const promoValid = !!ref && scopeAllows(c.promoOffer, t);
    if (ref && promoValid) {
        const recalculated = promoDiscountFor(c.promoOffer, t.promoBase != null ? t.promoBase : t.grossPrice);
        const due = recalculated != null ? recalculated : parts.promo;   // unknown offer shape: keep the AED amount
        const amt = take(due);
        if (amt > 0) { fields.redeemedPromoCode = amt; carried.push({ kind: 'promo', amount: amt }); }
        else if (p.redeemedPromoCode != null) fields.redeemedPromoCode = n(p.redeemedPromoCode);
        if (p.promoCode != null) fields.promoCode = p.promoCode;
        for (const k of PROMO_REWARD_FIELDS) if (p[k] !== undefined) fields[k] = p[k];
        if (p.bogoVoucherId && !bogoRecordId(p)) fields.bogoVoucherId = p.bogoVoucherId; // BOGO-type promo: offer id
        if (due - amt > 0.009) dropped.push({ kind: 'promo', amount: round2(due - amt), reason: 'exceedsNewPrice' });
    } else if (ref || parts.promo > 0) {
        const reason = !ref ? 'unknownDiscount' : (c.promoOffer ? 'promoNotValidForNewProvider' : 'promoOfferNotFound');
        const hasReward = PROMO_REWARD_FIELDS.some((k) => p[k] !== undefined && p[k] !== null && p[k] !== 0);
        if (parts.promo > 0 || hasReward) dropped.push({ kind: hasReward && !(parts.promo > 0) ? 'promoReward' : 'promo', amount: parts.promo, reason });
    }

    // 2. wallet voucher — all or nothing, minAmount against the pre-discount price
    let releaseVoucherId = null;
    if (parts.voucher > 0) {
        const minAmount = n(c.voucherRecord && c.voucherRecord.minAmount);
        if (minAmount > n(t.grossPrice)) { dropped.push({ kind: 'voucher', amount: parts.voucher, reason: 'belowVoucherMinAmount' }); releaseVoucherId = p.voucher_id; }
        else if (parts.voucher > remaining + 0.009) { dropped.push({ kind: 'voucher', amount: parts.voucher, reason: 'exceedsNewPrice' }); releaseVoucherId = p.voucher_id; }
        else {
            const amt = take(parts.voucher);
            fields.voucher_id = p.voucher_id; fields.redeemedVoucher = amt;
            carried.push({ kind: 'voucher', amount: amt });
        }
    }

    // 3. wallet BOGO — scoped by its wallet record
    let releaseBogoRecordId = null;
    if (parts.bogo > 0) {
        const id = bogoRecordId(p);
        if (!scopeAllows(c.bogoRecord, t)) {
            dropped.push({ kind: 'bogo', amount: parts.bogo, reason: c.bogoRecord ? 'bogoNotValidForNewProvider' : 'bogoRecordNotFound' });
            releaseBogoRecordId = id;
        } else {
            const amt = take(parts.bogo);
            if (amt > 0) {
                if (p.bogo_id) fields.bogo_id = p.bogo_id;
                fields.bogoVoucherId = id; fields.bogoDiscountAmount = amt; fields.redeemedBogo = amt;
                carried.push({ kind: 'bogo', amount: amt });
            } else { dropped.push({ kind: 'bogo', amount: parts.bogo, reason: 'exceedsNewPrice' }); releaseBogoRecordId = id; }
        }
    }

    // 4. loyalty points, 5. wallet credit — never scoped, capped at what is left
    const excess = { points: 0, credit: 0 };
    if (parts.points > 0) {
        const amt = take(parts.points);
        excess.points = round2(parts.points - amt);
        if (amt > 0) { fields.redeemedPoints = amt; carried.push({ kind: 'points', amount: amt }); }
        if (p.pointsPalance !== undefined) fields.pointsPalance = round2(n(p.pointsPalance) + excess.points);
        if (excess.points > 0) dropped.push({ kind: 'points', amount: excess.points, reason: 'exceedsNewPrice' });
    }
    if (parts.credit > 0) {
        const amt = take(parts.credit);
        excess.credit = round2(parts.credit - amt);
        if (amt > 0) { fields.redeemedCredit = amt; carried.push({ kind: 'credit', amount: amt }); }
        if (excess.credit > 0) dropped.push({ kind: 'credit', amount: excess.credit, reason: 'exceedsNewPrice' });
    }

    const discount = round2(carried.reduce((s, x) => s + x.amount, 0));
    if (discount > 0) fields.discount = discount;

    // codeId / code = the last applied part, in checkout order (the trigger keys redemption on them)
    const has = (k) => carried.some((x) => x.kind === k);
    if (has('credit')) { fields.codeId = 'wallet'; fields.code = 'wallet'; }
    else if (has('points')) { fields.codeId = 'loyalty'; fields.code = 'loyalty'; }
    else if (has('bogo')) { fields.codeId = 'bogo'; fields.code = 'bogo'; }
    else if (has('voucher')) { fields.codeId = 'voucher'; fields.code = 'voucher'; }
    else if (ref && promoValid) {
        fields.codeId = !isSpecialCode(p.codeId) && p.codeId ? p.codeId : ((c.promoOffer && c.promoOffer.id) || p.codeId);
        fields.code = !isSpecialCode(p.code) && p.code ? p.code : (p.promoCode || (c.promoOffer && c.promoOffer.code) || p.code);
    }
    for (const k of Object.keys(fields)) if (fields[k] === undefined) delete fields[k];

    return { fields, discount, carried, dropped, releaseVoucherId, releaseBogoRecordId, excess };
}

/** Replace every discount field on `price` with the resolved ones (mutates and returns price). */
function applyCarriedDiscounts(price, resolved) {
    for (const k of DISCOUNT_FIELDS) delete price[k];
    Object.assign(price, resolved.fields);
    return price;
}

/** Load what resolveCarriedDiscounts needs. `db` is an admin or client (v8) Firestore. */
async function loadDiscountContext(db, booking) {
    const b = booking || {};
    const p = b.price || {};
    const walletLog = b.customer_id ? db.collection('users').doc(b.customer_id).collection('walletLog') : null;
    const read = async (ref) => { try { const s = await ref.get(); return s.exists ? { id: s.id, ...s.data() } : null; } catch (e) { return null; } };
    const ctx = { promoOffer: null, voucherRecord: null, bogoRecord: null };
    const ref = promoOfferRef(p);
    if (ref && ref.id) ctx.promoOffer = await read(db.collection('offers').doc(ref.id));
    if (ref && !ctx.promoOffer) {
        const code = ref.code || p.promoCode;
        for (const variant of [...new Set([code, lower(code)].filter(Boolean))]) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const s = await db.collection('offers').where('code', '==', variant).limit(1).get();
                if (!s.empty) { ctx.promoOffer = { id: s.docs[0].id, ...s.docs[0].data() }; break; }
            } catch (e) { /* not found */ }
        }
    }
    if (walletLog && p.voucher_id) ctx.voucherRecord = await read(walletLog.doc(String(p.voucher_id)));
    const bid = bogoRecordId(p);
    if (walletLog && bid) ctx.bogoRecord = await read(walletLog.doc(String(bid)));
    return ctx;
}

export {
    DISCOUNT_FIELDS, SPECIAL_CODES, promoOfferRef, bogoRecordId, scopeAllows, discountParts, offerDiscountType, promoDiscountFor, resolveCarriedDiscounts, applyCarriedDiscounts, loadDiscountContext,
};
